import React, { useContext, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { FormContext, useFormDisable } from '../../../../../../shared/form-new/form'
import { FormNewText } from '../../../../../../shared/form-new/form-text'
import { FormNewSelect } from '../../../../../../shared/form-new/form-select'
import { FormNewLabel } from '../../../../../../shared/form-new/form-label'
import { LookupPanel } from '../../../../../../shared/sso-workspace/lookup-panel'
import { FromIssuerTag } from '../../../../../../shared/sso-workspace/from-issuer-tag'
import { StaffTag } from '../../../../../../shared/sso-workspace/workspace-shell'
import { IconControl } from '../../../../../../shared/sso-workspace/icon-control'
import { FormNewOrganizationSelect, FormNewActiveSwitch, CONNECTION_OPTIONS } from './fields'

const FILLED_LABELS = { tenantClaim: 'tenant claim', tenantValue: 'tenant value', signoutURL: 'the sign-out endpoint' }

// The identity provider section: who people sign in with, over OpenID
// Connect (a connection's only protocol: Microsoft, Google, Apple and
// Facebook for everyone are the Sign-in methods page's). In customer mode
// the app registration, status and client id are hidden (spec: customer
// view). `showOwner` is set only by the add page, which shows the owner
// picker here because the routing section can't render before the row
// exists. `status` (servingNow, testers, connectionName, domains) feeds the
// Status switch's sentence and confirmation. `issuerLocked` (the
// organization's own connection page): the
// issuer and tenant are shown, never edited and never looked up -- they are
// how EducationXR's shared app trusts the organization's tokens.
// `onLoadIcons` (issuer -> auth's icon library) and `loadedIcon` (the icon
// the connection was loaded with) feed the staff Icon control.
// `tenantLocked` (a connection on EducationXR's shared app, SSO sign-in
// safety checks spec 12.1 item 2): its Microsoft tenant is set by its
// organization's approval, so the issuer, client id, tenant claim and value
// are shown read-only, the App registration can't be changed and there's no
// Look up; a save still sends them as they were (auth refuses any change,
// 409 provider.shared_app_tenant_locked).
export const TENANT_LOCKED_NOTE = "This connection's Microsoft tenant is set by its organization's approval. To use another tenant, send a new approval request."

export const SectionIdentity = ({ mode, onLookup, usedClaims, onJump, onDiscovered, onFilled, showOwner, noRoute, onFixRoute, issuerLocked, tenantLocked, status = {}, onLoadIcons, loadedIcon }) => {
  const { watch, getValues, setValue, register } = useFormContext()
  const formDisabled = useFormDisable()
  const { formEnabled } = useContext(FormContext)
  const issuer = watch('issure')
  const connection = watch('connection')
  const staff = mode !== 'customer'
  // The issuer and tenant are never edited here, and never looked up.
  const locked = Boolean(issuerLocked || tenantLocked)
  const [filled, setFilled] = useState({ keys: [], pulse: 0 })
  // LookupPanel calls onApply from the closure of the render that started
  // the run, so the pulse counter lives in a ref rather than that closure.
  const pulse = useRef(0)
  // Read through a ref for the same reason. A read-only user may still look
  // up the issuer (it only reads), but nothing is written into the form.
  const readOnly = useRef(!formEnabled)
  readOnly.current = !formEnabled

  // Fills only empty fields -- never overwrites what an admin typed -- and
  // reports what it filled so the panel can say so and the tags can show.
  const handleApply = discovery => {
    const keys = []
    const fill = !readOnly.current
    if (fill && !getValues('tenantValue') && discovery.tenantValue) {
      setValue('tenantClaim', discovery.tenantClaim, { shouldDirty: true })
      setValue('tenantValue', discovery.tenantValue, { shouldDirty: true })
      keys.push('tenantClaim', 'tenantValue')
    }
    if (fill && !getValues('signoutURL') && discovery.endSessionEndpoint) {
      setValue('signoutURL', discovery.endSessionEndpoint, { shouldDirty: true })
      keys.push('signoutURL')
    }
    pulse.current += 1
    const next = { keys, pulse: pulse.current }
    setFilled(next)
    onFilled && onFilled(next)
    onDiscovered && onDiscovered(discovery)
    return keys.map(k => FILLED_LABELS[k])
  }

  const tag = key => <FromIssuerTag show={filled.keys.includes(key)} pulseKey={filled.pulse} />
  const staffLabel = text => <React.Fragment>{text}<StaffTag /></React.Fragment>

  return (
    <div className="row g-3">
      <div className="col-md-6"><FormNewText name="providerName" label="Name" placeholder="e.g. Microsoft Entra ID" required subtext="Only admins see this." /></div>
      <div className="col-md-6">
        <FormNewText name="displayName" label="Sign-in button label" placeholder="e.g. Sign in with Microsoft" required subtext="What people see on the sign-in page." />
      </div>
      {staff &&
        <React.Fragment>
          <div className="col-md-6">
            {tenantLocked
              // Shown, not a field: its value stays the form's as loaded and
              // goes back with the save unchanged.
              ? <FormNewLabel name="connection" label={staffLabel('App registration')}>
                  <select id="connection" className="form-select" value={connection || ''} disabled onChange={() => {}}>
                    {CONNECTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </FormNewLabel>
              : <FormNewSelect name="connection" label={staffLabel('App registration')} required options={CONNECTION_OPTIONS} />}
          </div>
          <div className="col-md-6"><FormNewActiveSwitch label={staffLabel('Status')} noRoute={noRoute} onFix={onFixRoute} {...status} /></div>
          {showOwner && <div className="col-md-6"><FormNewOrganizationSelect /></div>}
        </React.Fragment>}

      {tenantLocked && <div className="col-12"><p className="text-muted small mb-0" data-testid="tenant-locked-note">{TENANT_LOCKED_NOTE}</p></div>}
      <div className="col-12">
        <FormNewLabel name="issure" label="Issuer" required subtext={<React.Fragment>Paste the issuer or its <code>/.well-known/openid-configuration</code> address.</React.Fragment>}>
          {/* The input shares a row with the Look up button, so FormNewLabel can't
              link it; it names its own label. The row goes in as an array so
              FormNewLabel doesn't also put aria-labelledby on the wrapper div. */}
          {[<div key="issuer-row" className="d-flex gap-2 align-items-start flex-wrap">
            <input id="issure" aria-labelledby="issure-label" className="form-control sw-mono" style={{ flex: '1 1 240px', width: 'auto', minWidth: 0 }} {...register('issure', { required: !locked })} readOnly={locked} disabled={formDisabled} />
            {!locked && <LookupPanel issuer={issuer} onLookup={onLookup} onApply={handleApply} usedClaims={usedClaims} onJump={onJump} />}
          </div>]}
        </FormNewLabel>
      </div>
      {staff && <div className="col-12">
        {tenantLocked
          ? <FormNewLabel name="clientID" label={staffLabel('Client ID')}><input id="clientID" className="form-control sw-mono" {...register('clientID')} readOnly disabled={formDisabled} /></FormNewLabel>
          : <FormNewText name="clientID" label={staffLabel('Client ID')} placeholder="The application (client) ID from the identity provider" required />}
      </div>}
      <div className="col-md-6"><FormNewLabel name="tenantClaim" label={<React.Fragment>Tenant claim{tag('tenantClaim')}</React.Fragment>} subtext="Only tokens from this tenant are accepted."><input id="tenantClaim" className="form-control sw-mono" {...register('tenantClaim')} readOnly={locked} disabled={formDisabled} /></FormNewLabel></div>
      <div className="col-md-6"><FormNewLabel name="tenantValue" label={<React.Fragment>Tenant value{tag('tenantValue')}</React.Fragment>}><input id="tenantValue" className="form-control sw-mono" {...register('tenantValue')} readOnly={locked} disabled={formDisabled} /></FormNewLabel></div>
      {staff && <div className="col-12"><FormNewText name="scopesExtra" label={staffLabel('Additional scopes')} placeholder="Space-separated, e.g. offline_access" /></div>}

      {staff && <div className="col-12"><IconControl onLoadIcons={onLoadIcons} loaded={loadedIcon} staffLabel={staffLabel} /></div>}
    </div>
  )
}
